import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Subject, switchMap, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlaybackService } from '../../../../core/services/playback.service';
import { WebPlaybackTrack } from '../../../../shared/models/spotify.models';
import { AIService, GenerationContext } from '../../../../core/services/ai.service';
import { BackendStorageService, PlaylistMetadata } from '../../../../core/services/backend-storage.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { CastService } from '../../../../core/services/cast.service';
import { DeviceSelectorDialogComponent } from '../device-selector-dialog/device-selector-dialog.component';

@Component({
  selector: 'app-expanded-player-dialog',
  templateUrl: './expanded-player-dialog.component.html',
  styleUrls: ['./expanded-player-dialog.component.scss']
})
export class ExpandedPlayerDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private progressInterval: any;
  private volumeTimeout: any;

  // State properties
  currentTrack: WebPlaybackTrack | null = null;
  isPlaying: boolean = false;
  progress: number = 0;
  duration: number = 0;
  volume: number = 0.5;
  isPlayerReady: boolean = false;
  isPremium: boolean = false;
  isDragging: boolean = false;
  repeatMode: number = 0; // 0: off, 1: track, 2: context
  isWebSDKActive: boolean = false;
  isRemoteStale = false; // another device claims playback but is dead
  hasOtherDevices: boolean = false;

  // Tracks the playlist (if any) the current track is being played from, plus
  // whether that playlist has been toggled "dynamic" by the user. Drives
  // visibility of the Generate Similar button.
  contextPlaylistId: string | null = null;
  contextPlaylistIsDynamic = false;

  constructor(
    private playbackService: PlaybackService,
    private dialogRef: MatDialogRef<ExpandedPlayerDialogComponent>,
    private aiService: AIService,
    private snackBar: MatSnackBar,
    private router: Router,
    private castService: CastService,
    private dialog: MatDialog,
    private backendStorage: BackendStorageService
  ) {}

  ngOnInit(): void {
    this.subscribeToPlaybackState();
    this.subscribeToPlaylistContext();
    this.startProgressTracking();
    this.checkForOtherDevices();
  }

  private subscribeToPlaylistContext(): void {
    this.playbackService.currentPlaylistContextId$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(playlistId => {
          this.contextPlaylistId = playlistId;
          if (!playlistId) {
            return of(null);
          }
          return this.backendStorage.getPlaylistPreference(playlistId);
        })
      )
      .subscribe((prefs: PlaylistMetadata | null) => {
        this.contextPlaylistIsDynamic = !!prefs?.isDynamic;
      });
  }

  // Show Generate Similar:
  //  - if there's no playlist context, button is shown (legacy "Dynamic Playlist" fallback)
  //  - if there's a playlist context, only show when it's marked dynamic
  get canShowGenerateSimilar(): boolean {
    return !this.contextPlaylistId || this.contextPlaylistIsDynamic;
  }

  ngOnDestroy(): void {
    this.stopProgressTracking();
    this.clearVolumeTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToPlaybackState(): void {
    // Subscribe to player ready state
    this.playbackService.isReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReady => {
        this.isPlayerReady = isReady;
        if (isReady) {
          this.checkForOtherDevices();
        }
      });

    // Subscribe to premium status
    this.playbackService.isPremium$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isPremium => {
        this.isPremium = isPremium;
      });

    // Subscribe to combined playback state (SDK + cross-device polling)
    this.playbackService.combinedPlaybackState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.currentTrack = state.currentTrack;
          this.isPlaying = state.isPlaying;
          // Only update progress if not dragging the slider
          if (!this.isDragging) {
            this.progress = state.progress;
          }
          this.duration = state.duration;
          this.isWebSDKActive = state.isWebSDKActive;
          this.isRemoteStale = state.isStale === true;
          this.repeatMode = state.repeatMode;
        } else {
          // Nothing playing anywhere. Keeping the old track with isPlaying=true
          // showed pause buttons for silence and fed the expanded player a
          // stale view (Oct 13 report: gray screen).
          this.currentTrack = null;
          this.isPlaying = false;
          this.isRemoteStale = false;
          this.progress = 0;
          this.duration = 0;
        }
      });

    // Get initial volume
    this.loadInitialVolume();
  }

  private startProgressTracking(): void {
    this.stopProgressTracking();
    
    this.progressInterval = setInterval(async () => {
      try {
        if (this.isPlaying && !this.isDragging && this.isPlayerReady) {
          const state = await this.playbackService.getCurrentState();
          if (state && !state.paused) {
            this.progress = state.position;
            this.duration = state.duration;
          }
        }
      } catch (error) {
        console.warn('Error getting current state for progress tracking:', error);
      }
    }, 1000);
  }

  private stopProgressTracking(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  // Control methods
  async togglePlay(): Promise<void> {
    if (this.isPlayerReady && this.isWebSDKActive) {
      // Local player is the active device (togglePlay itself pauses via the
      // Web API or takes over when it has nothing loaded).
      try {
        await this.playbackService.togglePlay();
      } catch (error) {
        console.error('Expanded player toggle play error:', error);
        this.snackBar.open('Playback control failed', 'Close', { duration: 4000 });
      }
      return;
    }
    if (this.isRemoteStale || !this.currentTrack) {
      // The "playing" device is dead, or nothing is loaded anywhere — start here.
      await this.takeOverWithFeedback();
      return;
    }
    // Control the remote device through the backend.
    const wantsPlay = !this.isPlaying;
    try {
      if (wantsPlay) {
        const response = await fetch(`${environment.apiUrl}/spotify/player/play`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        if (!response.ok) {
          throw new Error(`Remote play failed (${response.status})`);
        }
      } else {
        // Through the service so our own intent / pending auto-resume can't
        // restart the music afterwards.
        await this.playbackService.pauseActiveDevice();
      }
      // Update state immediately for better UX
      this.isPlaying = wantsPlay;
    } catch (error) {
      if (wantsPlay) {
        // The remote device didn't answer a PLAY — it's gone (Oct 13: a dead
        // tab). Start here rather than leaving the press doing nothing.
        console.warn('Expanded player remote play failed, taking over here:', error);
        await this.takeOverWithFeedback();
      } else {
        // A failed PAUSE must never start music on this phone.
        console.warn('Expanded player remote pause failed:', error);
        this.snackBar.open('Could not pause the playing device', 'Close', { duration: 4000 });
      }
    }
  }

  private async takeOverWithFeedback(): Promise<void> {
    try {
      await this.playbackService.takeOverPlayback();
    } catch (error) {
      console.error('Expanded player takeover failed:', error);
      this.snackBar.open('Could not start playback on this device', 'Close', { duration: 4000 });
    }
  }

  async previousTrack(): Promise<void> {
    try {
      if (this.isPlayerReady && this.isWebSDKActive) {
        // Use Web Playback SDK only if it's the active device
        await this.playbackService.previousTrack();
      } else {
        // Use backend API for remote devices or when SDK is not active
        const response = await fetch(`${environment.apiUrl}/spotify/player/previous`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error('Failed to skip to previous track');
        }
      }
    } catch (error) {
      console.error('Expanded player previous track error:', error);
      this.snackBar.open('Could not reach the playing device — press play to move playback here', 'Close', { duration: 4000 });
    }
  }

  async nextTrack(): Promise<void> {
    try {
      if (this.isPlayerReady && this.isWebSDKActive) {
        // Use Web Playback SDK only if it's the active device
        await this.playbackService.nextTrack();
      } else {
        // Use backend API for remote devices or when SDK is not active
        const response = await fetch(`${environment.apiUrl}/spotify/player/next`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        });
        
        if (!response.ok) {
          throw new Error('Failed to skip to next track');
        }
      }
    } catch (error) {
      console.error('Expanded player next track error:', error);
      this.snackBar.open('Could not reach the playing device — press play to move playback here', 'Close', { duration: 4000 });
    }
  }

  async toggleRepeat(): Promise<void> {
    if (!this.isPlayerReady) return;

    try {
      const modes = ['off', 'track', 'context'] as const;
      const nextModeIndex = (this.repeatMode + 1) % 3;
      await this.playbackService.setRepeatMode(modes[nextModeIndex]);
    } catch (error) {
      console.error('Expanded player toggle repeat error:', error);
    }
  }

  // Progress control
  getProgressPercentage(): number {
    if (this.duration <= 0) return 0;
    return (this.progress / this.duration) * 100;
  }

  onSeekStart(): void {
    this.isDragging = true;
  }

  // Material 17's <input matSliderThumb> fires native DOM events, so the value
  // lives on event.target, not event.value.
  private readSliderValue(event: Event): number | null {
    const target = event.target as HTMLInputElement | null;
    if (!target) return null;
    const value = target.valueAsNumber;
    return Number.isFinite(value) ? value : null;
  }

  onSeekChange(event: Event): void {
    if (this.duration <= 0) return;
    const value = this.readSliderValue(event);
    if (value === null) return;
    // Set isDragging on the first input so the polling interval stops
    // overwriting our optimistic UI position mid-interaction.
    this.isDragging = true;
    this.progress = (value / 100) * this.duration;
  }

  async onSeekEnd(event: Event): Promise<void> {
    this.isDragging = false;

    if (!this.isPlayerReady || this.duration <= 0) return;

    const value = this.readSliderValue(event);
    if (value === null) return;
    const newPosition = (value / 100) * this.duration;

    try {
      await this.playbackService.seek(newPosition);
    } catch (error) {
      console.error('Expanded player seek error:', error);
    }
  }

  private async loadInitialVolume(): Promise<void> {
    try {
      const volume = await this.playbackService.getVolume();
      this.volume = volume;
    } catch (error) {
      console.error('Failed to get initial volume:', error);
      this.volume = 0.5; // Default fallback
    }
  }

  // Volume input - update UI only (no API call)
  onVolumeInput(event: Event): void {
    const value = this.readSliderValue(event);
    if (value === null) return;
    this.volume = value / 100;
  }

  // Volume change - API call when user finishes dragging
  async onVolumeChange(event: Event): Promise<void> {
    if (!this.isPlayerReady) return;

    const value = this.readSliderValue(event);
    if (value === null) return;
    const volume = value / 100;
    this.volume = volume;

    try {
      await this.playbackService.setVolume(volume);
    } catch (error) {
      console.error('Expanded player volume change error:', error);
      // Revert volume on error
      try {
        const currentVolume = await this.playbackService.getVolume();
        this.volume = currentVolume;
      } catch (getError) {
        console.error('Failed to revert volume:', getError);
      }
    }
  }

  private clearVolumeTimeout(): void {
    if (this.volumeTimeout) {
      clearTimeout(this.volumeTimeout);
      this.volumeTimeout = null;
    }
  }

  // Dialog control
  closeDialog(): void {
    this.dialogRef.close();
  }

  // Utility methods
  getArtistNames(): string {
    if (!this.currentTrack?.artists) return '';
    return this.currentTrack.artists.map(artist => artist.name).join(', ');
  }

  getAlbumImage(): string | null {
    if (!this.currentTrack?.album?.images || this.currentTrack.album.images.length === 0) {
      return null;
    }
    
    // Return the largest image for expanded player
    const sortedImages = this.currentTrack.album.images.sort((a, b) => (b.width || 0) - (a.width || 0));
    return sortedImages[0]?.url || null;
  }

  formatTime(ms: number): string {
    return this.playbackService.formatDuration(ms);
  }

  getRepeatIcon(): string {
    switch (this.repeatMode) {
      case 1: return 'repeat_one';
      case 2: return 'repeat';
      default: return 'repeat';
    }
  }

  getRepeatTooltip(): string {
    switch (this.repeatMode) {
      case 0: return 'Enable repeat';
      case 1: return 'Repeat current track';
      case 2: return 'Repeat playlist';
      default: return 'Repeat';
    }
  }

  // State getters for template
  get hasTrack(): boolean {
    return !!this.currentTrack;
  }

  async generateSimilarPlaylist(): Promise<void> {
    if (!this.currentTrack) return;

    // Bail if visible-state and gating disagree (defensive — the *ngIf in the
    // template should prevent this).
    if (!this.canShowGenerateSimilar) return;

    try {
      this.snackBar.open('Generating playlist...', 'Close', { duration: 3000 });

      const targetingContextPlaylist = !!this.contextPlaylistId && this.contextPlaylistIsDynamic;
      const trackName = this.currentTrack.name;
      const artistNames = this.getArtistNames();

      const context: GenerationContext = {
        type: 'similar_song',
        song: { title: trackName, artist: artistNames },
        userRequest: `Generate songs similar to "${trackName}" by ${artistNames}`,
        createPlaylist: true,
        playlistId: targetingContextPlaylist ? this.contextPlaylistId! : undefined,
        playlistDescription: `Songs similar to ${trackName}`,
        // No playlistName when targeting an existing playlist; legacy fallback
        // path uses the hardcoded "Dynamic Playlist" name on the backend.
      };

      const response = await this.aiService.generatePlaylist(context);

      if (!response.success || !response.spotifyPlaylist) {
        throw new Error(response.error || 'Failed to create playlist');
      }

      this.closeDialog();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/playlists', response.spotifyPlaylist!.id]);
      });

      this.snackBar.open(
        targetingContextPlaylist ? 'Playlist regenerated' : 'Dynamic Playlist updated',
        'Dismiss',
        { duration: 3000 }
      );

    } catch (error: any) {
      console.error('Error generating similar playlist:', error);
      this.snackBar.open(error.message || 'Failed to generate playlist', 'Close', { duration: 5000 });
    }
  }

  // Check for other devices
  private async checkForOtherDevices(): Promise<void> {
    if (!this.isPlayerReady || !this.isPremium) {
      this.hasOtherDevices = false;
      return;
    }

    try {
      const response = await this.playbackService.getAvailableDevices();
      const hasSpotifyDevices = response.devices.some(device => !device.is_active);
      const hasCastDevices = this.castService.isAvailable() && !this.castService.isConnected();
      
      // Check if there are any non-active devices (other devices we can cast to)
      this.hasOtherDevices = hasSpotifyDevices || hasCastDevices;
    } catch (error) {
      console.warn('Failed to check for other devices:', error);
      this.hasOtherDevices = false;
    }
  }

  // Open device selector dialog
  openDeviceSelector(): void {
    this.dialog.open(DeviceSelectorDialogComponent, {
      width: '400px',
      maxWidth: '95vw',
      maxHeight: '80vh',
      panelClass: 'device-selector-dialog-panel',
      disableClose: false,
      hasBackdrop: true,
      backdropClass: 'device-selector-backdrop'
    });
  }
}